import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount, useDisconnect, useSignMessage } from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Loader2, CheckCircle, Copy, ExternalLink, Shield, Download, QrCode, Ban, Clock, Users, Gift } from 'lucide-react';
import { usePWAReconnect } from '../hooks/usePWAReconnect';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ============================================================================
// TURNSTILE WIDGET COMPONENT
// ============================================================================

const TurnstileWidget = ({ siteKey, onVerify, onError, onExpire }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  
  useEffect(() => {
    if (!window.turnstile) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad';
      script.async = true;
      
      window.onTurnstileLoad = () => {
        console.log('[Turnstile] Script loaded, rendering widget');
        renderWidget();
      };
      
      script.onerror = () => {
        console.error('[Turnstile] Failed to load script');
        if (onError) onError();
      };
      
      document.head.appendChild(script);
    } else {
      renderWidget();
    }
    
    function renderWidget() {
      if (containerRef.current && window.turnstile && !widgetIdRef.current) {
        console.log('[Turnstile] Rendering widget with siteKey:', siteKey);
        try {
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: siteKey,
            callback: (token) => {
              console.log('[Turnstile] Verification successful');
              if (onVerify) onVerify(token);
            },
            'error-callback': (error) => {
              console.error('[Turnstile] Error callback triggered:', error);
              if (onError) onError(error);
            },
            'expired-callback': () => {
              console.log('[Turnstile] Token expired');
              if (onExpire) onExpire();
            },
            theme: 'light'
          });
          console.log('[Turnstile] Widget rendered, ID:', widgetIdRef.current);
        } catch (err) {
          console.error('[Turnstile] Render error:', err);
          if (onError) onError(err);
        }
      }
    }
    
    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, onVerify, onError, onExpire]);
  
  return <div ref={containerRef} data-testid="turnstile-widget" style={{ minHeight: '65px' }} />;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function RegisterQRPage() {
  const navigate = useNavigate();
  
  const { address, isConnected } = useAccount();
  const { disconnect } = useDisconnect();
  const { signMessageAsync } = useSignMessage();
  const { openConnectModal } = useConnectModal();
  const { isReconnecting, isPWA, connectWithFlag } = usePWAReconnect(openConnectModal);
  
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('connect');
  const [error, setError] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [label, setLabel] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // ============================================================================
  // WALLET DETECTION
  // - Desktop: Always show ConnectButton
  // - Mobile + wallet detected (via UA or window object): Show ConnectButton
  // - Mobile + no wallet: Show instructions
  // ============================================================================
  const [walletCheckComplete, setWalletCheckComplete] = useState(false);
  const [hasWallet, setHasWallet] = useState(false);
  
  const isMobile = typeof window !== 'undefined' && /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent || '');
  
  useEffect(() => {
    const ua = navigator.userAgent || '';
    
    // Layer 1: Check User Agent for wallet browser signatures (instant, no race condition)
    const walletUASignatures = [
      'Trust',           // Trust Wallet browser
      'MetaMask',        // MetaMask browser  
      'TokenPocket',     // TokenPocket
      'imToken',         // imToken
      'Coinbase',        // Coinbase Wallet
    ];
    
    const isWalletBrowserUA = walletUASignatures.some(sig => ua.includes(sig));
    
    if (!isMobile || isWalletBrowserUA) {
      // Desktop OR wallet browser detected via UA - show ConnectButton immediately
      console.log('[RegisterQR] Desktop or wallet UA detected:', isWalletBrowserUA ? 'wallet browser' : 'desktop');
      setHasWallet(true);
      setWalletCheckComplete(true);
      return;
    }
    
    // Layer 2: Check window objects with retries (for wallets that don't modify UA)
    let attempts = 0;
    const maxAttempts = 6;
    
    const checkWallet = () => {
      attempts++;
      if (window.ethereum || window.trustwallet?.ethereum || window.coinbaseWalletExtension) {
        console.log('[RegisterQR] Wallet provider found via window object');
        setHasWallet(true);
        setWalletCheckComplete(true);
        return;
      }
      if (attempts >= maxAttempts) {
        console.log('[RegisterQR] No wallet detected - showing instructions');
        setHasWallet(false);
        setWalletCheckComplete(true);
        return;
      }
      setTimeout(checkWallet, 100);
    };
    
    checkWallet();
  }, [isMobile]);
  
  // Load config
  useEffect(() => {
    async function loadConfig() {
      try {
        const res = await fetch(`${API_URL}/api/qr/config/registration`);
        const data = await res.json();
        setConfig(data);
        
        if (!data.enabled) {
          setError('QR registration is temporarily unavailable. Please try again later.');
          setStep('error');
        }
      } catch (err) {
        console.error('Failed to load config:', err);
        setError('Failed to load registration configuration');
        setStep('error');
      } finally {
        setLoading(false);
      }
    }
    loadConfig();
  }, []);
  
  // Update step when wallet connects — check if QR already exists first
  useEffect(() => {
    if (!isConnected || !address) return;
    
    async function checkExistingQR() {
      try {
        const res = await fetch(`${API_URL}/api/qr/wallet/${address}`);
        if (res.ok) {
          const data = await res.json();
          if (data.exists) {
            // QR already created (likely from a previous attempt that survived the reload)
            setResult(data);
            setStep('success');
            return;
          }
        }
      } catch { /* continue to normal flow */ }
      
      // No existing QR — proceed to registration
      if (step === 'connect') setStep('captcha');
    }
    
    checkExistingQR();
  }, [isConnected, address, step]);
  
  const handleTurnstileVerify = useCallback((token) => {
    console.log('[RegisterQR] Turnstile verified');
    setTurnstileToken(token);
    setError(null);
  }, []);
  
  const handleTurnstileError = useCallback((err) => {
    console.error('[RegisterQR] Turnstile error:', err);
  }, []);
  
  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
  }, []);
  
  const turnstileRequired = !!config?.turnstileSiteKey;

  const handleRegister = useCallback(async () => {
    if (!isConnected || (turnstileRequired && !turnstileToken)) {
      console.log('[RegisterQR] Cannot register: isConnected=', isConnected, 'turnstileToken=', !!turnstileToken, 'turnstileRequired=', turnstileRequired);
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      const timestamp = Date.now();
      const message = `TipMe QR Registration\n\nI want to create a QR code for my wallet to receive tips.\n\nWallet: ${address}\nTimestamp: ${timestamp}`;
      
      console.log('[RegisterQR] Requesting signature...');
      
      let signature;
      try {
        signature = await signMessageAsync({ message });
        console.log('[RegisterQR] Signature obtained');
      } catch (signError) {
        console.error('[RegisterQR] Signature failed:', signError);
        if (signError.message?.includes('rejected') || signError.message?.includes('denied') || signError.code === 4001) {
          setError('Signature request was rejected. Please try again and approve the signature.');
        } else {
          setError(`Signature failed: ${signError.message || 'Unknown error'}`);
        }
        setSubmitting(false);
        return;
      }
      
      console.log('[RegisterQR] Sending registration request to API...');
      
      const res = await fetch(`${API_URL}/api/qr/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet: address,
          signature,
          message,
          turnstileToken,
          label: label || undefined
        })
      });
      
      console.log('[RegisterQR] API response status:', res.status);
      
      const data = await res.json();
      console.log('[RegisterQR] API response data:', data);
      
      if (!res.ok) {
        if (data.detail === 'CAPTCHA_FAILED') {
          setError('CAPTCHA verification failed. Please refresh and try again.');
        } else if (data.detail === 'INVALID_SIGNATURE') {
          setError('Signature verification failed. Please try again.');
        } else {
          setError(data.detail || 'Registration failed');
        }
        setSubmitting(false);
        return;
      }
      
      setResult(data);
      setStep('success');
      
    } catch (err) {
      console.error('[RegisterQR] Registration error:', err);
      setError(`Registration failed: ${err.message || 'Please try again.'}`);
      setSubmitting(false);
    }
  }, [isConnected, turnstileToken, turnstileRequired, address, signMessageAsync, label]);
  
  const copyToClipboard = useCallback(async () => {
    if (!result?.tipUrl) return;
    const fullUrl = `${window.location.origin}${result.tipUrl}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  }, [result]);
  
  const downloadQRCode = () => {
    const svg = document.getElementById('qr-code-svg');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    const scale = 4;
    canvas.width = 256 * scale;
    canvas.height = 256 * scale;
    
    img.onload = () => {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      const link = document.createElement('a');
      link.download = `tipme-qr-${result.qrId}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#ccc8bc] flex items-center justify-center p-4">
        <Loader2 className="h-12 w-12 animate-spin text-[#f0b90b]" />
      </div>
    );
  }
  
  // Error state (config not available) - redirect to production
  if (step === 'error' && !config?.enabled) {
    return (
      <div className="min-h-screen bg-[#ccc8bc] flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <Card className="bg-[#e8e3db] border-0 shadow-lg p-8 text-center rounded-2xl">
            <QrCode className="h-16 w-16 text-[#f0b90b] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#1a1a1a] mb-2">Get Your Personal QR Code</h2>
            <p className="text-[#666] mb-6">Create a unique QR code to receive tips in person, at events, or anywhere you meet your supporters.</p>
            <Button 
              onClick={() => window.location.href = 'https://tipme.space/register-qr'}
              className="bg-[#f0b90b] hover:bg-[#d4a50a] text-white rounded-xl"
            >
              Register QR Code
            </Button>
          </Card>
        </div>
      </div>
    );
  }
  
  // Success state
  if (step === 'success' && result) {
    const fullUrl = `${window.location.origin}${result.tipUrl}`;
    
    return (
      <div className="min-h-screen bg-[#ccc8bc] flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <Card className="bg-[#e8e3db] border-0 shadow-lg p-8 rounded-2xl">
            <div className="text-center mb-6">
              <CheckCircle className="h-16 w-16 text-[#22c55e] mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-[#1a1a1a] mb-2">
                {result.existing ? 'Welcome Back!' : 'QR Created!'}
              </h2>
              <p className="text-[#666]">
                {result.existing 
                  ? 'Your QR code is always available here.' 
                  : result.message}
              </p>
            </div>
            
            {/* QR Code */}
            <div className="flex justify-center mb-6">
              <div className="bg-white p-4 rounded-2xl shadow-md">
                <QRCodeSVG
                  id="qr-code-svg"
                  value={fullUrl}
                  size={180}
                  level="H"
                  includeMargin={true}
                  bgColor="#FFFFFF"
                  fgColor="#000000"
                />
              </div>
            </div>
            
            {/* Download Button */}
            <Button
              onClick={downloadQRCode}
              className="w-full mb-4 bg-[#f0b90b] hover:bg-[#d4a50a] text-white rounded-xl h-12"
            >
              <Download className="h-4 w-4 mr-2" />
              Download QR Code
            </Button>
            
            {/* Tip URL */}
            <div className="bg-[#d4cfc4] rounded-xl p-3 mb-4">
              <p className="text-xs text-[#666] mb-1">Your Tip Link</p>
              <div className="flex items-center gap-2">
                <p className="font-mono text-sm text-[#1a1a1a] truncate flex-1">{fullUrl}</p>
                <button
                  onClick={copyToClipboard}
                  className="text-[#666] hover:text-[#1a1a1a]"
                >
                  {copied ? <CheckCircle className="h-4 w-4 text-[#22c55e]" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            {/* Verification prompt */}
            {!result.isVerified && (
              <div className="bg-[#f0b90b]/10 border border-[#f0b90b]/30 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-[#f0b90b] shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[#1a1a1a] font-semibold text-sm">Want More Visibility?</p>
                    <p className="text-[#666] text-xs mt-1">
                      Verification builds trust and unlocks tipping on X & YouTube.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Actions */}
            <div className="space-y-2">
              <Button
                onClick={() => navigate(`/my-qr/${result.qrId}`)}
                variant="outline"
                className="w-full border-[#d4cfc4] text-[#1a1a1a] hover:bg-[#d4cfc4] rounded-xl h-12"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                View My QR Page
              </Button>
              
              {!result.isVerified && (
                <Button
                  onClick={() => navigate('/verify')}
                  variant="outline"
                  className="w-full border-[#f0b90b] text-[#f0b90b] hover:bg-[#f0b90b]/10 rounded-xl h-12"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Get Verified
                </Button>
              )}
            </div>
          </Card>
        </div>
      </div>
    );
  }
  
  // Main registration flow
  return (
    <div className="min-h-screen bg-[#ccc8bc] py-10 px-4">
      <div className="max-w-md mx-auto">
        {/* Why Create QR Section */}
        <Card className="bg-[#e8e3db] border-0 shadow-lg p-6 mb-6 rounded-2xl">
          <h2 className="text-xl font-bold text-[#1a1a1a] mb-4">Why Create a QR?</h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#f0b90b]/20 flex items-center justify-center shrink-0">
                <QrCode className="h-5 w-5 text-[#f0b90b]" />
              </div>
              <div>
                <p className="font-semibold text-[#1a1a1a]">In-Person Tipping</p>
                <p className="text-sm text-[#666]">Let people tip you at events, meetups, or IRL</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#f0b90b]/20 flex items-center justify-center shrink-0">
                <Users className="h-5 w-5 text-[#f0b90b]" />
              </div>
              <div>
                <p className="font-semibold text-[#1a1a1a]">No Verification Required</p>
                <p className="text-sm text-[#666]">Start receiving tips immediately</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#f0b90b]/20 flex items-center justify-center shrink-0">
                <Gift className="h-5 w-5 text-[#f0b90b]" />
              </div>
              <div>
                <p className="font-semibold text-[#1a1a1a]">Earn Rewards</p>
                <p className="text-sm text-[#666]">QR tips count toward TRUE point rewards</p>
              </div>
            </div>
          </div>
        </Card>
        
        {/* Registration Form */}
        <Card className="bg-[#e8e3db] border-0 shadow-lg p-6 rounded-2xl">
          {/* Step indicator */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              isConnected ? 'bg-[#22c55e] text-white' : 'bg-[#f0b90b] text-white'
            }`}>
              {isConnected ? <CheckCircle className="h-4 w-4" /> : '1'}
            </div>
            <div className={`w-16 h-1 rounded ${isConnected ? 'bg-[#22c55e]' : 'bg-[#d4cfc4]'}`} />
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              (turnstileToken || !turnstileRequired) ? 'bg-[#22c55e] text-white' : 
              isConnected ? 'bg-[#f0b90b] text-white' : 'bg-[#d4cfc4] text-[#666]'
            }`}>
              {(turnstileToken || !turnstileRequired) ? <CheckCircle className="h-4 w-4" /> : '2'}
            </div>
            <div className={`w-16 h-1 rounded ${(turnstileToken || !turnstileRequired) ? 'bg-[#22c55e]' : 'bg-[#d4cfc4]'}`} />
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              (turnstileToken || !turnstileRequired) ? 'bg-[#f0b90b] text-white' : 'bg-[#d4cfc4] text-[#666]'
            }`}>
              3
            </div>
          </div>
          
          {/* Step 1: Connect Wallet */}
          {!isConnected ? (
            <div className="text-center">
              <h3 className="text-lg font-bold text-[#1a1a1a] mb-2">Connect Your Wallet</h3>
              <p className="text-[#666] text-sm mb-4">Connect your BSC wallet to create a QR code</p>
              
              {/* PWA Reconnecting state */}
              {isReconnecting ? (
                <div className="flex flex-col items-center py-4" data-testid="pwa-reconnecting">
                  <Loader2 className="h-8 w-8 animate-spin text-[#f0b90b] mb-2" />
                  <p className="text-[#1a1a1a] font-medium text-sm">Finishing connection...</p>
                  <p className="text-[#666] text-xs mt-1">Restoring your wallet session</p>
                </div>
              ) : !walletCheckComplete ? (
                // Brief loading while checking for wallet
                <div className="flex flex-col items-center py-4">
                  <Loader2 className="h-8 w-8 animate-spin text-[#f0b90b] mb-2" />
                  <p className="text-[#666] text-sm">Loading...</p>
                </div>
              ) : hasWallet ? (
                // Desktop OR Mobile with wallet - show ConnectButton
                <ConnectButton.Custom>
                  {({
                    account,
                    chain,
                    openConnectModal: rkOpenModal,
                    authenticationStatus,
                    mounted,
                  }) => {
                    const ready = mounted && authenticationStatus !== 'loading';
                    
                    return (
                      <div
                        {...(!ready && {
                          'aria-hidden': true,
                          'style': {
                            opacity: 0,
                            pointerEvents: 'none',
                            userSelect: 'none',
                          },
                        })}
                      >
                        <Button
                          onClick={() => connectWithFlag(rkOpenModal)}
                          className="w-full bg-[#f0b90b] hover:bg-[#d4a50a] text-white rounded-xl h-12"
                          data-testid="connect-wallet-btn"
                        >
                          Connect Wallet
                        </Button>
                      </div>
                    );
                  }}
                </ConnectButton.Custom>
              ) : (
                // Mobile without wallet - show instructions
                <div className="space-y-4">
                  <div className="bg-[#f0b90b]/10 border border-[#f0b90b]/30 rounded-xl p-4 text-left">
                    <p className="text-[#1a1a1a] font-semibold text-sm mb-2">📱 Open in Wallet Browser</p>
                    <p className="text-[#666] text-xs mb-3">
                      To create a QR code, open this page from your wallet's built-in browser:
                    </p>
                    <div className="bg-white/50 rounded-lg p-3 space-y-2">
                      <ol className="text-[#666] text-xs space-y-1 list-decimal list-inside">
                        <li>Open your wallet app (MetaMask, Trust Wallet, etc.)</li>
                        <li>Find the <span className="font-medium">Browser</span> tab inside the wallet</li>
                        <li>Navigate to: <span className="font-mono text-[#f0b90b]">tipme.space/register-qr</span></li>
                      </ol>
                    </div>
                  </div>
                  
                  {/* Copy URL button */}
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText('https://tipme.space/register-qr');
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className="w-full bg-[#1a1a1a] hover:bg-[#333] text-white rounded-xl h-12"
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        URL Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        Copy URL to Paste in Wallet Browser
                      </>
                    )}
                  </Button>
                  
                  <p className="text-[#666] text-xs text-center">
                    Already in wallet browser? <button 
                      onClick={() => {
                        setHasWallet(true);
                      }}
                      className="text-[#f0b90b] underline"
                    >
                      Try connecting anyway
                    </button>
                  </p>
                </div>
              )}
              
              {/* Error display */}
              {error && (
                <p className="text-red-500 text-sm mt-3">{error}</p>
              )}
            </div>
          ) : (
            <>
              {/* Connected wallet display */}
              <div className="bg-[#d4cfc4] rounded-xl p-3 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-[#666]">Wallet</p>
                    <p className="font-mono text-sm text-[#1a1a1a]">
                      {address?.slice(0, 6)}...{address?.slice(-4)}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      disconnect();
                      setStep('connect');
                      setTurnstileToken(null);
                    }}
                    className="text-sm text-[#666] hover:text-[#1a1a1a] underline"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
              
              {/* Label input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#1a1a1a] mb-1">
                  Label (optional)
                </label>
                <Input
                  type="text"
                  placeholder="e.g., My Tips, Coffee Fund"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  className="bg-[#d4cfc4] border-[#b8b3ab] text-[#1a1a1a]"
                  maxLength={50}
                />
              </div>
              
              {/* Step 2: CAPTCHA */}
              {config?.turnstileSiteKey && (
                <div className="mb-4">
                  <h3 className="text-lg font-bold text-[#1a1a1a] mb-2 text-center">
                    Verify You're Human
                  </h3>
                  <p className="text-[#666] text-sm mb-4 text-center">Complete the CAPTCHA below</p>
                  
                  <div className="flex justify-center bg-[#d4cfc4] rounded-xl p-4">
                    <TurnstileWidget
                      siteKey={config.turnstileSiteKey}
                      onVerify={handleTurnstileVerify}
                      onError={handleTurnstileError}
                      onExpire={handleTurnstileExpire}
                    />
                  </div>
                  
                  {turnstileToken && (
                    <div className="flex items-center justify-center gap-2 text-[#22c55e] text-sm mt-2">
                      <CheckCircle className="h-4 w-4" />
                      Verified
                    </div>
                  )}
                </div>
              )}
              
              {/* Step 3: Create Button */}
              <Button
                onClick={handleRegister}
                disabled={(!turnstileToken && turnstileRequired) || submitting}
                className="w-full bg-[#f0b90b] hover:bg-[#d4a50a] text-white disabled:opacity-50 rounded-xl h-12"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <QrCode className="h-4 w-4 mr-2" />
                    Create My QR Code
                  </>
                )}
              </Button>
              
              {/* Error display */}
              {error && (
                <div className="mt-4 bg-red-100 border border-red-300 rounded-xl p-3 text-red-700 text-sm">
                  {error}
                </div>
              )}
            </>
          )}
          
          {/* Info note */}
          <div className="mt-6 pt-4 border-t border-[#d4cfc4]">
            <div className="flex items-center gap-2 text-[#666] text-xs">
              <Clock className="h-4 w-4" />
              <span>One QR code per wallet. Creation is instant.</span>
            </div>
          </div>
        </Card>
        
        {/* Link to verification */}
        <div className="mt-6 text-center">
          <p className="text-[#666] text-sm">
            Already have a QR?{' '}
            <button
              onClick={() => navigate('/verify')}
              className="text-[#f0b90b] hover:underline font-semibold"
            >
              Get Verified
            </button>
            {' '}to unlock social tipping.
          </p>
        </div>
      </div>
    </div>
  );
}
